import React, { useEffect, useState } from "react";
import { callAPI } from "../../../helper/apiUtils";
import {
  formatEmailPlainBody,
  isCollapsedEmailStub,
  linkifyPlainText,
  looksLikeHtml,
  repairMashedEmailHtml,
} from "../../../helper/emailBodyFormat";
import { formatRelativeDay } from "../cockpitFormat";

function messageFromResponse(res) {
  const data = res?.data || res;
  if (!data || typeof data !== "object") return null;
  if (data.status === false && !data.message) return null;
  if (data.message && typeof data.message === "object") return data.message;
  if (data.id != null || data.subject !== undefined) return data;
  return null;
}

function bodyDocument(email) {
  const html = email?.bodyHtml || email?.html || "";
  const text = email?.bodyText || "";
  const fallback = email?.body || email?.snippet || "";
  let raw = "";
  let isHtml = false;
  if (looksLikeHtml(html) && !isCollapsedEmailStub(html)) {
    raw = repairMashedEmailHtml(html);
    isHtml = true;
  } else if (looksLikeHtml(text) && !isCollapsedEmailStub(text)) {
    raw = repairMashedEmailHtml(text);
    isHtml = true;
  } else {
    raw = formatEmailPlainBody([text, html, fallback].find((v) => String(v || "").trim()) || "");
  }
  const safeHtml = isHtml
    ? String(raw)
        .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, "")
        .replace(/\son\w+\s*=\s*(['"]).*?\1/gi, "")
    : "";
  if (isHtml && /<html[\s>]/i.test(safeHtml)) return safeHtml;
  const body = isHtml
    ? safeHtml
    : `<pre style="white-space:pre-wrap;font-family:inherit;margin:0;">${linkifyPlainText(raw)}</pre>`;
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><base target="_blank"><style>
    html,body{margin:0;padding:0;}
    body{font-family:"Segoe UI",system-ui,sans-serif;font-size:14px;line-height:1.55;color:#1c2430;word-wrap:break-word;}
    img,table{max-width:100%;}
    img{height:auto;}
    a{color:#e36a12;}
  </style></head><body>${body}</body></html>`;
}

function hasBody(email) {
  return !!(email && (email.bodyHtml || email.html || email.bodyText || email.body || email.snippet));
}

function partyLine(item, strings) {
  if (!item) return "";
  if (item.folder === "SENT") {
    const tos = Array.isArray(item.toAddresses) ? item.toAddresses : [];
    const who = tos.map((t) => t.name || t.email).filter(Boolean).join(", ");
    return who ? `${strings.email_to || "An"}: ${who}` : strings.email_folder_sent || "Gesendet";
  }
  const from = item.fromAddress || {};
  const who = from.name || from.email || "";
  return who ? `${strings.email_from || "Von"}: ${who}` : "";
}

/**
 * Expanded email text under a cockpit row. The row itself is the accordion header.
 */
export default function CockpitEmailReader({ item, strings, language }) {
  const [email, setEmail] = useState(item || null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!item?.id) return undefined;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    setEmail(item);
    (async () => {
      try {
        const params = {};
        if (item.emailAccountId) params.accountId = item.emailAccountId;
        if (item.folder) params.folder = item.folder === "SENT" ? "SENT" : "INBOX";
        const res = await callAPI(`/email/messages/${encodeURIComponent(item.id)}`, params, "GET");
        const message = messageFromResponse(res);
        if (cancelled) return;
        if (!message) {
          setFailed(true);
        } else {
          setEmail({ ...item, ...message, folder: item.folder });
        }
      } catch (_) {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [item]);

  const subject = (email && email.subject) || strings.email_no_subject || "(Kein Betreff)";
  const when = email ? formatRelativeDay(email.receivedAt, strings, language) : "";
  const doc = email && hasBody(email) ? bodyDocument(email) : "";

  return (
    <div className="cockpit-email-reader">
      <div className="cockpit-email-reader-meta">
        <span>{partyLine(email, strings)}</span>
        {when ? <span>{when}</span> : null}
      </div>
      {loading ? (
        <div className="cockpit-email-reader-status">{strings.cockpit_email_detail_loading || "E-Mail wird geladen …"}</div>
      ) : failed && !doc ? (
        <div className="cockpit-email-reader-status">{strings.cockpit_email_detail_failed || "E-Mail konnte nicht geladen werden."}</div>
      ) : !doc ? (
        <div className="cockpit-email-reader-status">{strings.cockpit_email_detail_empty || "Diese E-Mail hat keinen Text."}</div>
      ) : (
        <iframe className="cockpit-email-reader-frame" title={subject} sandbox="allow-popups allow-popups-to-escape-sandbox" srcDoc={doc} />
      )}
    </div>
  );
}
